import { useEffect, useState } from 'react';

/**
 * Light / dark / system.
 *
 * "System" is the default and a real option, not just the absence of a choice:
 * it means the app follows the desktop and changes with it. Choosing light or
 * dark stamps `data-theme` on the root element, which the CSS treats as
 * overriding the media query in both directions — so someone on a dark desktop
 * can still deliberately pick light.
 */

const STORAGE_KEY = 'seller.theme';
const CHANGE_EVENT = 'seller:theme-change';

export type ThemeChoice = 'system' | 'light' | 'dark';

function readStored(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    // Private windows and blocked site data throw on access rather than
    // returning null, so the read itself has to be guarded.
  }
  return 'system';
}

function apply(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', choice);
  }
}

export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readStored);

  useEffect(() => {
    apply(choice);
  }, [choice]);

  // Two toggles can be on screen at once — the header and the settings page —
  // and each holds its own state, so a change in one is announced to the rest.
  useEffect(() => {
    function onChange(event: Event) {
      setChoice((event as CustomEvent<ThemeChoice>).detail);
    }
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  function update(next: ThemeChoice) {
    setChoice(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The preference simply will not persist; the session still honours it.
    }
    window.dispatchEvent(new CustomEvent<ThemeChoice>(CHANGE_EVENT, { detail: next }));
  }

  return [choice, update];
}

const OPTIONS: { value: ThemeChoice; label: string; glyph: string }[] = [
  { value: 'light', label: 'Light', glyph: '☀' },
  { value: 'dark', label: 'Dark', glyph: '☾' },
  { value: 'system', label: 'Match system', glyph: '◐' },
];

export function ThemeToggle() {
  const [choice, setChoice] = useTheme();

  return (
    <div
      className="flex items-center rounded-md ring-1 ring-inset ring-line"
      role="group"
      aria-label="Color theme"
    >
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => setChoice(option.value)}
          title={option.label}
          aria-label={option.label}
          aria-pressed={choice === option.value}
          className={`px-2 py-1 text-xs first:rounded-l-md last:rounded-r-md ${
            choice === option.value
              ? 'bg-brand-600 text-brand-on'
              : 'text-muted hover:bg-sunken hover:text-body'
          }`}
        >
          <span aria-hidden>{option.glyph}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Applies the stored preference before React renders.
 *
 * Without this the first paint uses the system theme and then corrects itself,
 * which reads as a flash of the wrong colors on every load.
 */
export function applyStoredThemeEarly(): void {
  apply(readStored());
}
